import { makeStyles } from '@material-ui/styles';
import { ChangeEvent, useEffect, useState } from 'react';
import useUserStore from '@services/stores/UserStore';
import AccountNavigationLayout from '@components/account/NavigationLayout';
import NewportInput from '@components/forms/NewportInput';
import NewportForm from '@components/forms/NewportForm';
import { useTranslation } from '@tools/i18n';
import { LabelSection } from '@components/account/LabelSection';
import { UnprocessableContentResponse, VoyadoMember } from '@services/MyNewportService';
import If from '@components/misc/If';
import { GetServerSidePropsContext } from 'next';
import theme from '@components/theme';
import CallToActionButton from '@components/products/partials/CallToActionButton';
import { Checkbox, Radio } from '@material-ui/core';
import useApplicationStore from '@services/stores/ApplicationStore';
import { AccountEmailVerified } from '@components/account/AccountEmailVerified';
import { appDataCache } from '@services/ServerCacheManager';

const useStyles = makeStyles({
    header: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 20,
        marginBottom: 40,
    },
    wrapper: {
        maxWidth: 600,
    },
    detailsForm: {
        display: 'grid',
        // @ts-ignore
        [theme.breakpoints?.up('sm')]: {
            gridTemplateColumns: '1fr 1fr',
            maxWidth: 600,
            '& > h1, & > button': {
                gridColumn: 'span 2',
            },
        },
    },
    fullWidth: {
        width: '100%',
        gridColumn: 'span 2',
    },
    mandatoryAsterisk: {
        fontSize: '10px',
        transform: 'translate(3px, -1px)',
        position: 'absolute',
    },
    mandatoryText: {
        fontSize: '0.85em',
        textAlign: 'right',
        width: '100%',
        margin: 0,
        gridColumn: 1,
        // @ts-ignore
        [theme.breakpoints?.up('sm')]: {
            gridColumn: 2,
        },
    },
    radioText: {
        display: 'grid',
    },
    checkbox: {
        width: 'auto',
        display: 'flex',
        alignItems: 'center',
        '& > span': {
            paddingLeft: 0
        }
    },
    radiobox: {
        display: 'grid',
        gridTemplateColumns: 'min-content 1fr',
        gap: 10,
        width: '100%',
        cursor: 'pointer',
    },
    optionGroup: {
        display: 'flex',
        gap: 20,
    },
    stack: {
        display: 'grid',
        width: '100%',
        gridTemplateColumns: '1fr',
        // @ts-ignore
        [theme.breakpoints?.up('sm')]: {
            gridColumn: 'span 2',
        },
    },
    preferencesForm: {},
}, { name: 'AccountDetails' });

export default function AccountDetails() {
    const classes = useStyles();
    const user = useUserStore();
    const userId = useUserStore((state) => state.user?.voyado_id);
    const appMethods = useApplicationStore((state) => state.methods);
    const service = user.getService();
    const { t } = useTranslation();
    const [payload, setPayload] = useState<VoyadoMember>();
    const [loading, setLoading] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [pageError, setPageError] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{[key:string]: string[]} | null>(null);
    const [validationErrorsMessage, setValidationErrorsMessage] = useState<string|null>(null);

    const inputs: Array<keyof VoyadoMember> = [
        'first_name',
        'last_name',
        'street',
        'care_of',
        'zip_code',
        'city',
        'email',
        'mobile_phone',
    ];

    const overview: Array<keyof VoyadoMember> = [
        'first_name',
        'last_name',
        'street',
        'zip_code',
        'city',
        'email',
        'mobile_phone',
    ];

    useEffect(() => {
        fetchData();
    }, [user.isLoggedIn]);

    function fetchData() {
        if (!userId) {
            return;
        }

        setIsDone(false);
        setLoading(true);

        const response = service.getEditableAccountData(userId);
        response.then((res) => {
            if (res.status === 'success') {
                setPayload(res.data);
                setPageError(false);
            } else {
                setPageError(true);
            }
        }).finally(() => setLoading(false));
    }

    async function handleSubmit() {
        if (!userId || !payload) {
            return;
        }

        setLoading(true);
        setValidationErrors(null);

        const info = await service.postEditableAccountData(userId, 'info', payload);
        const communications = await service.postEditableAccountData(userId, 'communication', payload);

        if (info.status === 'error' || communications.status === 'error') {
            appMethods.notify('errors:generic-update-error', {
                title: 'errors:generic-error-title',
            });

            if (info.response?.status === 422) {
                info.response.json().then((res: UnprocessableContentResponse) => {
                    debugger;
                    setValidationErrors(res.errors);
                    setValidationErrorsMessage(res.message);
                })
            }
        }

        if (info.status === 'success' && communications.status === 'success') {
            setPayload((prev) => {
                return {
                    ...info.data,
                    contact_preferences_accepts_sms: prev?.contact_preferences_accepts_sms || false,
                    contact_preferences_accepts_email: prev?.contact_preferences_accepts_email || false,
                };
            });
            setIsDone(true);
        }

        setLoading(false);
    }

    function handle(event: ChangeEvent | Event | MouseEvent | any, key: keyof VoyadoMember, value?: any) {
        setPayload((prev) => {
            if (!prev) {
                return prev;
            }

            // @ts-ignore
            prev[key] = value !== undefined ? value : event.target.value;
            return { ...prev };
        });
    }

    function createInput(key: keyof VoyadoMember) {
        const mandatory = key !== 'care_of' ? ' *' : ''
        return <NewportInput
            label={t(`account:${key.replaceAll('_', '-')}`) + mandatory}
            value={payload?.[key] || ''}
            onChange={(event) => handle(event, key)}
            disabled={isDisabled(key)}
            errors={validationErrors?.[key]}
        ></NewportInput>;
    }

    function isDisabled(key: keyof VoyadoMember) {
        return key === 'email';
    }

    /**
     * @deprecated
     */
    function renderHeader() {
        return <header className={classes.header}>
            {overview.map((key) => {
                return <LabelSection
                    // @ts-ignore
                    label={t(`account:${key?.replaceAll('_', '-')}`)}
                    value={payload?.[key] as string}
                />;
            })}
        </header>;
    }

    function renderOptions() {
        return <section className={classes.stack}>
                <p>{t('account:checkbox-type-of-communication')}</p>
                <section className={classes.optionGroup}>
                    <div className={classes.checkbox}>
                        <Checkbox
                            checked={payload?.contact_preferences_accepts_email}
                            onChange={(event) => handle(event, 'contact_preferences_accepts_email', !payload?.contact_preferences_accepts_email)}>
                        </Checkbox>
                        {t('account:checkbox-accepts-email')}
                    </div>
                    <div className={classes.checkbox}>
                        <Checkbox
                            checked={payload?.contact_preferences_accepts_sms}
                            onChange={(event) => handle(event, 'contact_preferences_accepts_sms', !payload?.contact_preferences_accepts_sms)}>
                        </Checkbox>
                        {t('account:checkbox-accepts-sms')}
                    </div>
                </section>
        </section>;
    }

    return <AccountNavigationLayout
        title={t('account:navigation-details')}
        loading={loading}
        error={pageError}
    >
        <AccountEmailVerified />
        <If condition={user.isLoggedIn && payload?.id}>
            <section className={classes.wrapper}>
                    <NewportForm className={classes.detailsForm} onSubmit={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleSubmit();
                    }}>
                        {inputs.map((key) => {
                            return createInput(key);
                        })}

                        <p className={classes.mandatoryText}>* {t('account:required-field')}</p>

                        {renderOptions()}

                        <CallToActionButton
                            type={'submit'}
                            isLoading={loading}
                            isDone={isDone}
                        >
                            {t('account:update-button')}
                        </CallToActionButton>
                    </NewportForm>
            </section>
        </If>
    </AccountNavigationLayout>;
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const appData = await appDataCache(context);
    return {
        props: {
            appData,
        },
    };
};
